import {
  Badge,
  Button,
  Card,
  CaseDescription,
  CaseSteps,
  DataTable,
  ErrorState,
  Facts,
  Loading,
  Modal,
  Note,
  Options,
  TEST_RESULTS,
  TestResultChip,
  testResultHelp,
  errorMessage,
  formatDate,
  formatDateTime,
  groupEnvironments,
  QaProgress,
  useToast,
} from "@corecrm/ui";
import { Fragment, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useMe,
  useOpenTestSessions,
  usePlanning,
  useQaProgress,
  useStartTestSession,
  useTestCase,
  useTestCases,
  useTestEnvironment,
  useTestEnvironments,
  useTestRuns,
  type TestCaseFilters,
} from "../api/queries";
import type {
  Project,
  TestBugRef,
  TestCase,
  TestCaseState,
  TestEnvironment,
  TestPriority,
  TestResult,
} from "../api/types";
import { DEVICE_LABEL, HIDDEN_RESULTS, PRIORITY_LABEL, readyAssigned, startable } from "../lib/testing";
import { RecordUnavailable, closeRecord } from "./bits";
import { TestSessionScreen } from "./TestSession";

/* The project's Testing tab (docs/test-suite.md section 8): every client-visible test case with its
   result per environment, a My tests filter, the environments and a case's run history (read-only),
   and running the tests assigned to you. Nothing here sends a notification. */

const STATE_OPTIONS: [string, string][] = [
  ["active", "Active"],
  ["functionality_changed", "Functionality Changed"],
  ["retired", "Retired"],
  ["all", "All"],
];

export function testingPath(projectId: string, extra = "") {
  return `/projects/${projectId}?view=testing${extra}`;
}

/** A bug key, linking to its portal page when the contact can see bugs. */
export function BugKey({ project, bug }: { project: Project; bug: TestBugRef }) {
  // A bug of another project (a maintenance project's) has no page under this one.
  return project.can_view_bugs && bug.project_id === project.id ? (
    <Link className="mono" to={`/projects/${project.id}/bugs/${bug.id}`}>
      {bug.key}
    </Link>
  ) : (
    <span className="mono">{bug.key}</span>
  );
}

export function TestingView({ project }: { project: Project }) {
  const [params] = useSearchParams();
  const session = params.get("session");
  if (session && project.can_run_tests) return <TestSessionScreen project={project} sessionId={session} />;
  return <TestingList project={project} />;
}

/** The sprints of one phase as picker options (only when the project shares its sprints). */
function SprintOptions({ project, phase }: { project: Project; phase: Project["phases"][number] }) {
  const plan = usePlanning(project.id, phase.id);
  const sprints = plan.data?.sprints ?? [];
  if (sprints.length === 0) return null;
  return (
    <optgroup label={`Sprints in ${phase.name}`}>
      {sprints.map((s) => (
        <option key={s.id} value={`sprint:${s.id}`}>
          {s.display_name}
        </option>
      ))}
    </optgroup>
  );
}

/** How testing is going (docs/test-suite.md section 14): the whole project, a phase or, when the
    team shares its sprints, a sprint. */
function QaReport({ project }: { project: Project }) {
  const [value, setValue] = useState("project");
  const [scope, scopeId] = value.split(":") as ["project" | "phase" | "sprint", string | undefined];
  const q = useQaProgress(project.id, scope, scopeId ?? null);
  const phases = [...project.phases].sort((a, b) => a.sequence - b.sequence);
  return (
    <div className="stack">
      <label className="flt">
        Show QA progress for
        <select aria-label="Show QA progress for" value={value} onChange={(e) => setValue(e.target.value)}>
          <option value="project">The whole project</option>
          {phases.length > 0 && (
            <optgroup label="Phases">
              {phases.map((p) => (
                <option key={p.id} value={`phase:${p.id}`}>
                  {p.name}
                </option>
              ))}
            </optgroup>
          )}
          {project.client_sees_sprints &&
            phases.map((p) => <SprintOptions key={p.id} project={project} phase={p} />)}
        </select>
      </label>
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <QaProgress data={q.data} />
      )}
    </div>
  );
}

function TestingList({ project }: { project: Project }) {
  const [environmentId, setEnvironmentId] = useState("");
  const [result, setResult] = useState<TestResult | "">("");
  const [state, setState] = useState("active");
  const [priority, setPriority] = useState<TestPriority | "">("");
  const [mine, setMine] = useState(false);
  const userId = useMe().data?.id;
  const [viewingEnv, setViewingEnv] = useState<string | null>(null);
  // The open case is in the address (/projects/:id/tests/:caseId), so it can be copied and shared.
  const { caseId: viewing } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const setViewing = (id: string | null) =>
    id
      ? navigate(`/projects/${project.id}/tests/${id}`, { state: { fromList: true } })
      : closeRecord(navigate, location.state, testingPath(project.id));

  const filters: TestCaseFilters = {
    ...(environmentId ? { environmentId } : {}),
    ...(result ? { result } : {}),
    state: state === "all" ? [] : [state as TestCaseState],
    ...(priority ? { priority } : {}),
    mine,
  };
  const cases = useTestCases(project.id, filters);
  const environments = useTestEnvironments(project.id);
  return (
    <div className="stack">
      <QaReport project={project} />
      {project.can_run_tests && <RunMyTests project={project} />}
      <div className="filters">
        <label className="flt">
          Environment
          <select
            aria-label="Environment"
            value={environmentId}
            onChange={(e) => setEnvironmentId(e.target.value)}
          >
            <option value="">All</option>
            {groupEnvironments(environments.data ?? []).map((g) => (
              <optgroup key={g.type} label={`${g.label} (${g.items.length})`}>
                <Options list={g.items.map((e) => [e.id, `${e.code} · ${e.name}`])} />
              </optgroup>
            ))}
          </select>
        </label>
        <label className="flt">
          Result
          <select
            aria-label="Result"
            value={result}
            onChange={(e) => setResult(e.target.value as TestResult)}
          >
            <option value="">All</option>
            <Options
              list={(Object.keys(TEST_RESULTS) as TestResult[])
                .filter((r) => !HIDDEN_RESULTS.includes(r))
                .map((r) => [r, TEST_RESULTS[r].label, testResultHelp(r)])}
            />
          </select>
        </label>
        <label className="flt">
          State
          <select aria-label="State" value={state} onChange={(e) => setState(e.target.value)}>
            <Options list={STATE_OPTIONS} />
          </select>
        </label>
        <label className="flt">
          Priority
          <select
            aria-label="Priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as TestPriority)}
          >
            <option value="">All</option>
            <Options list={Object.entries(PRIORITY_LABEL)} />
          </select>
        </label>
        <label className="check">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
          <span>My tests</span>
        </label>
      </div>
      {cases.isPending ? (
        <Loading />
      ) : cases.error ? (
        <ErrorState error={cases.error} onRetry={() => void cases.refetch()} />
      ) : (
        <DataTable
          label="Test cases"
          rows={cases.data ?? []}
          rowKey={(c) => c.id}
          empty={mine ? "No tests are assigned to you." : "No tests to show."}
          columns={[
            {
              header: "Test",
              cell: (c) => (
                <button type="button" className="task-title" onClick={() => setViewing(c.id)}>
                  <span className="mono faint">{c.code}</span> <strong>{c.title}</strong>
                </button>
              ),
            },
            { header: "Story", cell: (c) => c.story.key || "—" },
            {
              header: "Results",
              cell: (c) => <LinkChips test={c} environmentId={environmentId} userId={userId} />,
            },
          ]}
        />
      )}
      <EnvironmentsCard project={project} onOpen={setViewingEnv} />
      {viewing && <CaseDialog project={project} caseId={viewing} onClose={() => setViewing(null)} />}
      {viewingEnv && <EnvironmentDialog id={viewingEnv} onClose={() => setViewingEnv(null)} />}
    </div>
  );
}

/** The result in each environment: a chip with the environment's name. */
function LinkChips({
  test,
  environmentId,
  userId,
}: {
  test: TestCase;
  environmentId: string;
  userId: string | undefined;
}) {
  const links = environmentId ? test.links.filter((l) => l.environment.id === environmentId) : test.links;
  if (!links.length) return <span className="faint">Not in an environment yet</span>;
  return (
    <span className="row" style={{ flexWrap: "wrap" }}>
      {links.map((l) => (
        <span key={l.id} className="row">
          <span className="faint">{l.environment.name}</span>
          <TestResultChip
            result={l.current_result}
            title={l.assignee ? `${l.environment.name}, assigned to ${l.assignee.name}` : l.environment.name}
          />
          {userId && l.assignee?.id === userId && <Badge kind="acc">Yours</Badge>}
        </span>
      ))}
    </span>
  );
}

/** The tests assigned to you that are ready, with Start on each environment that has some. */
function RunMyTests({ project }: { project: Project }) {
  const me = useMe().data;
  const toast = useToast();
  const navigate = useNavigate();
  const mine = useTestCases(project.id, { state: ["active"], mine: true });
  const environments = useTestEnvironments(project.id);
  const open = useOpenTestSessions(project.id);
  const start = useStartTestSession(project.id);
  if (mine.error ?? environments.error)
    return (
      <Card title="Your tests to run">
        <ErrorState
          error={mine.error ?? environments.error}
          onRetry={() => {
            void mine.refetch();
            void environments.refetch();
          }}
        />
      </Card>
    );
  if (!me || mine.isPending || environments.isPending) return null;
  const ready = readyAssigned(mine.data ?? [], me.id);
  const options = startable(ready, environments.data ?? []);
  const sessions = open.data ?? [];
  if (!options.length && !sessions.length) return null;
  const go = (id: string) => navigate(testingPath(project.id, `&session=${id}`));
  // Environments with tests ready, plus ones with a session left open and nothing ready any more
  // (count 0): all grouped by type, so every row is under its own heading and counted in it.
  const rows = [
    ...options,
    ...sessions
      .filter((s) => !options.some((o) => o.environment.id === s.environment.id))
      .map((s) => ({ environment: s.environment, count: 0 })),
  ].map((o) => ({ ...o, type: o.environment.type }));
  return (
    <Card title="Your tests to run">
      <div className="item-list">
        {groupEnvironments(rows).map((group) => (
          <Fragment key={group.type}>
            <h4 className="na-h env-group-h">
              {group.label} ({group.items.length})
            </h4>
            {group.items.map(({ environment: env, count }) => {
              const going = sessions.find((s) => s.environment.id === env.id);
              // Someone else's session is open here: only one at a time, so there is nothing to start.
              const other = environments.data?.find((e) => e.id === env.id)?.open_session;
              const taken = !going && !!other && !other.mine;
              return (
                <div className="item" key={env.id}>
                  <div className="what">
                    <strong>{env.name}</strong>
                    <span>
                      {DEVICE_LABEL[env.device]}
                      {count > 0
                        ? ` · ${count} ${count === 1 ? "test" : "tests"} ready for you`
                        : going
                          ? ` · Session started ${formatDateTime(going.started_at)}`
                          : ""}
                    </span>
                  </div>
                  {going ? (
                    <Button variant={count > 0 ? "primary" : undefined} onClick={() => go(going.id)}>
                      Continue testing
                    </Button>
                  ) : taken ? (
                    <span className="muted">Someone else is testing here. Check back when they finish.</span>
                  ) : (
                    <Button
                      variant="primary"
                      busy={start.isPending && start.variables === env.id}
                      onClick={() =>
                        start.mutate(env.id, {
                          onSuccess: (s) => go(s.session.id),
                          onError: (e) => toast(errorMessage(e), "err"),
                        })
                      }
                    >
                      Start testing
                    </Button>
                  )}
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>
    </Card>
  );
}

function EnvironmentsCard({ project, onOpen }: { project: Project; onOpen: (id: string) => void }) {
  const environments = useTestEnvironments(project.id);
  if (!environments.data?.length) return null;
  return (
    <Card title="Testing environments">
      <div className="item-list">
        {groupEnvironments(environments.data).map((group) => (
          <Fragment key={group.type}>
            <h4 className="na-h env-group-h">
              {group.label} ({group.items.length})
            </h4>
            {group.items.map((e) => (
              <div className="item" key={e.id}>
                <div className="what">
                  <button type="button" className="task-title" onClick={() => onOpen(e.id)}>
                    <span className="mono faint">{e.code}</span> <strong>{e.name}</strong>
                  </button>
                  <span>
                    {DEVICE_LABEL[e.device]}
                    {e.app_surface ? ` · ${e.app_surface}` : ""}
                    {e.profile ? ` · ${e.profile}` : ""}
                  </span>
                </div>
                <Badge kind={e.status === "ready" || e.status === "completed" ? "ok" : "mute"}>
                  {e.status === "ready" ? "Ready" : e.status === "completed" ? "Completed" : "Being set up"}
                </Badge>
              </div>
            ))}
          </Fragment>
        ))}
      </div>
    </Card>
  );
}

export function StepList({ steps, label }: { steps: { id: string; text: string }[]; label: string }) {
  if (!steps.length) return null;
  return (
    <div>
      <strong>{label}</strong>
      <ol className="task-desc" style={{ paddingLeft: 20 }}>
        {steps.map((s) => (
          <li key={s.id}>{s.text}</li>
        ))}
      </ol>
    </div>
  );
}

function Block({ title, text }: { title: string; text: string | null }) {
  if (!text) return null;
  return (
    <div>
      <strong>{title}</strong>
      <p className="task-desc" style={{ whiteSpace: "pre-wrap" }}>
        {text}
      </p>
    </div>
  );
}

/** What a tester needs to know about an environment: read-only. The sign-in user shows only when
    the team has filled it in. */
export function EnvironmentFacts({ env }: { env: TestEnvironment }) {
  const rows: [ReactNode, ReactNode][] = [
    ["Device", DEVICE_LABEL[env.device]],
    ...(env.app_surface ? ([["App", env.app_surface]] as [ReactNode, ReactNode][]) : []),
    ...(env.url
      ? ([
          [
            "Address",
            <a href={env.url} target="_blank" rel="noopener noreferrer">
              {env.url}
            </a>,
          ],
        ] as [ReactNode, ReactNode][])
      : []),
    ...(env.profile ? ([["Profile", env.profile]] as [ReactNode, ReactNode][]) : []),
    ...(env.browser ? ([["Browser", env.browser]] as [ReactNode, ReactNode][]) : []),
    ...(env.sign_in_user ? ([["Sign in as", env.sign_in_user]] as [ReactNode, ReactNode][]) : []),
    ...(env.credentials_hint ? ([["Password", env.credentials_hint]] as [ReactNode, ReactNode][]) : []),
  ];
  return <Facts items={rows} />;
}

function EnvironmentDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const env = useTestEnvironment(id);
  if (env.error) return <RecordUnavailable what="environment" error={env.error} onClose={onClose} />;
  const e = env.data;
  return (
    <Modal
      wide
      className="record"
      title={e ? `${e.code} · ${e.name}` : "Environment"}
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {!e ? (
        <Loading />
      ) : (
        <div className="stack">
          <EnvironmentFacts env={e} />
          <StepList steps={e.setup_steps} label="Set up before testing" />
          <StepList steps={e.reset_steps} label="Clean up after testing" />
          <Block title="Notes" text={e.notes} />
        </div>
      )}
    </Modal>
  );
}

/** One test case: its steps, expected result, environments with result and assignee, and the run
    history. Read-only. */
export function CaseDialog({
  project,
  caseId,
  onClose,
}: {
  project: Project;
  caseId: string;
  onClose: () => void;
}) {
  const found = useTestCase(caseId);
  const runs = useTestRuns(project.id, caseId);
  const c = found.data && found.data.project_id === project.id ? found.data : undefined;
  if (found.error || (found.data && !c))
    return <RecordUnavailable what="test" error={found.error} onClose={onClose} />;
  return (
    <Modal
      wide
      className="record"
      title={c ? `${c.code} · ${c.title}` : "Test"}
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {!c ? (
        <Loading />
      ) : (
        <div className="stack">
          <span className="row">
            <Badge kind={c.state === "active" ? "ok" : c.state === "retired" ? "mute" : "high"}>
              {c.state === "active" ? "Active" : c.state === "retired" ? "Retired" : "Functionality Changed"}
            </Badge>
            <Badge kind="info">{PRIORITY_LABEL[c.priority]} priority</Badge>
            {c.story.key && (
              <span className="faint">
                Story: {c.story.key} {c.story.title}
              </span>
            )}
          </span>
          <CaseDescription description={c.description} />
          {c.state === "functionality_changed" && (
            <Note kind="acc">
              The app now works differently on purpose, so this test is being rewritten.
              {c.change_note ? ` ${c.change_note}` : ""}
            </Note>
          )}
          <StepList steps={c.setup_steps} label="Set up for this test" />
          {c.format === "checklist" && c.checklist_steps ? (
            <div>
              <strong>Steps</strong>
              <CaseSteps
                checkable={false}
                steps={c.checklist_steps}
                label="Steps"
                expectedLabel="What should happen"
              />
            </div>
          ) : (
            <>
              <Block title="Steps" text={c.steps} />
              <Block title="What should happen" text={c.expected_result} />
            </>
          )}
          <div>
            <strong>Environments</strong>
            <DataTable
              label="Test environments"
              rows={c.links}
              rowKey={(l) => l.id}
              empty="Not in an environment yet."
              columns={[
                { header: "Environment", cell: (l) => l.environment.name },
                { header: "Result", cell: (l) => <TestResultChip result={l.current_result} /> },
                { header: "Assigned to", cell: (l) => l.assignee?.name ?? "No one yet" },
                {
                  header: "Bug",
                  cell: (l) =>
                    l.open_bug ? (
                      <BugKey project={project} bug={l.open_bug} />
                    ) : l.blocking_bug ? (
                      <span>
                        Blocked by <span className="mono">{l.blocking_bug.key}</span>
                      </span>
                    ) : (
                      "—"
                    ),
                },
              ]}
            />
          </div>
          <div>
            <strong>Run history</strong>
            {runs.isPending ? (
              <Loading />
            ) : (
              <DataTable
                label="Run history"
                rows={runs.data ?? []}
                rowKey={(r) => r.id}
                empty="Not run yet."
                columns={[
                  { header: "When", cell: (r) => formatDate(r.recorded_at) },
                  { header: "Environment", cell: (r) => r.environment.name },
                  { header: "Result", cell: (r) => <TestResultChip result={r.result} /> },
                  { header: "By", cell: (r) => r.recorded_by.name },
                  { header: "Notes", cell: (r) => r.notes ?? "—" },
                  {
                    header: "Bug",
                    cell: (r) => (r.bug ? <BugKey project={project} bug={r.bug} /> : "—"),
                  },
                ]}
              />
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
